import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { createStore } from '@ngneat/elf';
import { deleteEntities, hasEntity, selectActiveEntity, selectAllEntities, selectEntity, setActiveId, setEntities, upsertEntities, withActiveId, withActiveIds, withEntities } from '@ngneat/elf-entities';
import { PaginationData } from '@ngneat/elf-pagination';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ElfUtil } from '@tcc-mono/shared/utils';
import { RelationType } from '@tcc-mono/vandijk/shared/data-access/models';
import { RelationTypeService } from '@tcc-mono/vandijk/shared/data-access/vandijk-api';
import { BehaviorSubject, EMPTY, Observable, Subject, concatMap, distinctUntilChanged, from, identity, map, merge, switchMap, take, tap } from 'rxjs';

const store = createStore(
  { name: 'relationTypes' },
  withEntities<RelationType>(),
  withActiveId()
)

@Injectable({
  providedIn: 'root'
})
export class RelationTypeRepository {
  private readonly _relationTypeService: RelationTypeService = inject(RelationTypeService);

  private _params = new BehaviorSubject<Params>({});
  private _params$: Observable<Params> = this._params.asObservable();

  private _pagination: PaginationData<number>;

  public relationTypes$: Observable<RelationType[]> = store.pipe(selectAllEntities());
  public selectedRelationType$: Observable<RelationType> = store.pipe(selectActiveEntity());

  public selectRelationType = (id: number): Observable<RelationType> => store.pipe(selectEntity(id));
  public hasEntity = (id: number): boolean => store.query(hasEntity(id));

  public listenToParams = (): Observable<unknown> => {
    return this._params$
      .pipe(
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        map((params: Params) => ({
          ...params,
          page: 1
        })),
        switchMap((params: Params) => this._getRelationTypes(params)),
        tap((relationTypes: RelationType[]) => store.update(setEntities(relationTypes)))
      );
  }

  public scrollLoad(event: any): void { // this is an IonInfiniteCustomEvent, which does not exist as a model.. therefor any
    if (this._pagination.currentPage < this._pagination.lastPage) {

      const params = {
        ...this._params.value,
        page: (this._pagination?.currentPage ?? 0) + 1
      };

      this._getRelationTypes(params)
        .pipe(
          take(1),
          tap((relationTypes: RelationType[]) => store.update(upsertEntities(relationTypes)))
        )
        .subscribe({
          next: () => event.target.complete()
        });

    } else {
      event.target.complete();
    }
  }

  public updateParams(params: Params): void {
    const newParams = {
      ...this._params.value,
      page: 1,
      ...params, //on purpose this order if for some reason someone needs to overwrite the page
    };

    this._params.next(newParams);
  }

  public addRelationType = (relationType: Partial<RelationType>): Observable<RelationType> => {
    return this._relationTypeService.post(relationType)
      .pipe(
        map(({ data }) => data),
        tap((relationType) => store.update(upsertEntities(relationType))),
        tap((relationType) => this.setActiveRelationType(relationType.id))
      );
  }

  public updateRelationType = (relationTypeId: number, relationType: RelationType): Observable<RelationType> => {
    return this._relationTypeService.put(relationTypeId, relationType)
      .pipe(
        map(({ data }) => data),
        tap((relationType) => store.update(upsertEntities(relationType)))
      );
  }

  public getRelationType = (relationTypeId: number, setActive: boolean = true): Observable<RelationType> => {
    const call = this._relationTypeService.get(relationTypeId)
      .pipe(
        map(({ data }) => data),
        tap((relationType) => store.update(upsertEntities(relationType))),
        setActive ? tap((relationType) => this.setActiveRelationType(relationType.id)) : identity
      );

    return store.query(hasEntity(relationTypeId)) ? EMPTY : call;
  }

  public removeRelationType = (relationTypeId: number): Observable<unknown> => {
    return this._relationTypeService.delete(relationTypeId)
      .pipe(
        tap(() => store.update(deleteEntities(relationTypeId)))
      );
  }

  public setActiveRelationType = (relationTypeId: number): void => {
    store.update(setActiveId(relationTypeId));
  }

  private _getRelationTypes = (params: Params): Observable<RelationType[]> => {
    return this._relationTypeService.getAll(params)
      .pipe(
        tap((result) => this._pagination = ElfUtil.convertMetaToElfPagination(result.meta)),
        map(({ data }) => data)
      );
  }
}
